import React, { useEffect, useMemo, useRef, useState } from 'react';
import ReactSelect, { components, FormatOptionLabelMeta, MultiValueProps } from 'react-select';
import styled from 'styled-components';

import { InfiniteData, UseInfiniteQueryResult } from '@tanstack/react-query';

import CircularProgress from 'components/shared/CircularProgress';
import DismissibleChip from 'components/shared/DismissibleChip';
import FieldLabel from 'components/shared/form/FieldLabel';
import { customTheme, getDefaultStyles } from 'components/shared/form/Select/customStyles';
import { useDebounce } from 'hooks/useDebounce';
import { Paginated } from 'models/Paginated';
import { colors } from 'styles/theme/colors';
import { BodySmall } from 'styles/typography';
import CheckboxOffIcon from 'svg/CheckboxOffIcon';
import CheckboxOnIcon from 'svg/CheckboxOnIcon';
import DropdownCaretIcon from 'svg/DropdownCaretIcon';
import DropdownClearIcon from 'svg/DropdownClearIcon';

function isPagedResource<T>(
  x: undefined | UseInfiniteQueryResult<InfiniteData<Paginated<T>>> | T[]
): x is UseInfiniteQueryResult<InfiniteData<Paginated<T>>> {
  return (x && !Array.isArray(x)) ?? false;
}

// Every chip a select draws is a DismissibleChip, so a chip drawn outside a select can match it exactly.
export const MultiValue = ({
  children,
  data,
  removeProps,
  isFocused,
  isDisabled,
  selectProps,
}: MultiValueProps<any>) => (
  <InSelectChip
    focused={isFocused}
    disabled={isDisabled}
    removeProps={{
      ...(removeProps as React.ButtonHTMLAttributes<HTMLButtonElement>),
      // Backspace removes chips, so the remove buttons stay out of the input's tab order.
      tabIndex: -1,
      'aria-label': `Remove ${selectProps.getOptionLabel(data)}`,
    }}>
    {children}
  </InSelectChip>
);

const InSelectChip = styled(DismissibleChip)`
  margin: 2px;
`;

const LoadingContainer = styled.div`
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-basis: 100%;
  margin: 8px 0;
`;

function LoadingState() {
  return (
    <LoadingContainer>
      <CircularProgress color={colors.black25} size={24} thickness={2} />
    </LoadingContainer>
  );
}

type InfiniteScrollMenuListType = {
  isLoading: boolean;
  hasMore: boolean;
  onEndReached: () => void;
};
export function InfiniteScrollMenuList(props: { children: any; selectProps: InfiniteScrollMenuListType }) {
  const { hasMore = false, isLoading = false, onEndReached = () => {} } = props.selectProps;
  const menuListRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (menuListRef.current) {
      menuListRef.current.onscroll = ({ target }) => {
        if (!target) return;
        const castTarget = target as any;
        const distanceFromBottom = castTarget.scrollHeight - (castTarget.clientHeight + castTarget.scrollTop);
        if (distanceFromBottom < 100 && hasMore && !isLoading) {
          onEndReached();
        }
      };
    }
  }, [isLoading, hasMore, onEndReached]);
  return (
    <components.MenuList {...(props as any)} innerRef={menuListRef}>
      {props.children}
      {isLoading && <LoadingState />}
    </components.MenuList>
  );
}

type AllowedReactSelectProps = Partial<
  Omit<React.ComponentProps<typeof ReactSelect>, 'options' | 'onChange' | 'value' | 'getOptionValue' | 'getOptionLabel'>
>;
export type SelectType<T> = AllowedReactSelectProps & {
  options: T[] | UseInfiniteQueryResult<InfiniteData<Paginated<T>>>;
  populateDefault?: boolean;
  getOptionValue: (obj: T) => string;
  getOptionLabel?: (obj: T) => string;
  formatOptionLabel?: (obj: T, options?: FormatOptionLabelMeta<T>) => React.JSX.Element | null | string | any;
  placeholder?: string;
  onChange: (change: T) => void;
  value?: T | null;
  disabled?: boolean;
  hasError?: boolean;
  dataTestId?: string;
  onDebouncedSearch?: (search: string) => void;
  prependedOptions?: T[];
  appendedOptions?: T[];
  typeToSearchText?: string;
  groupBy?: (item: T) => string;
  getGroupOptionLabel?: (groupKey: string) => string;
};

// The full option list: prepended, then whatever was fetched or passed, then appended —
// deduplicated by option value with the first occurrence winning, so prepended and appended
// entries keep their positions.
//
// The dedupe is what makes `prependedOptions` safe to use unconditionally. It exists to keep the
// CURRENT value selectable when it might not be in the fetched list, and with an infinite list the
// caller cannot know whether it is: the matching entry may simply be on a page nobody has scrolled
// to yet. So callers always prepend and this collapses the overlap, rather than each caller
// guessing and getting it wrong.
//
// Without it, a referral whose source already matched a reporting name showed that name twice in
// the Edit Demographics picker (OLIO-3158, found in QA Sep 2) — once prepended, once from the page.
function buildOptions<T>(base: T[], prepended: T[], appended: T[], getOptionValue: (obj: T) => string) {
  const seen = new Set<string>();

  return prepended
    .concat(base)
    .concat(appended)
    .filter((option) => {
      const key = getOptionValue(option);
      if (seen.has(key)) return false;

      seen.add(key);
      return true;
    });
}

export function Select<T>(props: SelectType<T>) {
  const {
    options,
    populateDefault = false,
    getOptionLabel,
    formatOptionLabel,
    getOptionValue,
    placeholder = 'Select...',
    onChange,
    value,
    disabled = false,
    dataTestId,
    onDebouncedSearch,
    prependedOptions = [],
    appendedOptions = [],
    typeToSearchText = 'Type to search...',
    groupBy,
    getGroupOptionLabel,
    ...reactSelectProps
  } = props;

  const [search, setSearch] = useState<string>('');
  const debouncedSearch = useDebounce(search);
  const prevDebouncedSearch = useRef<string>(debouncedSearch);

  const pagedData = isPagedResource(options) ? options.data?.pages : undefined;
  const nonPagedOptions = isPagedResource(options) ? undefined : options;
  const allOptions = useMemo(() => {
    const base = pagedData ? pagedData.flatMap((p) => p.data) : (nonPagedOptions ?? []);
    return buildOptions(base, prependedOptions, appendedOptions, getOptionValue);
  }, [pagedData, nonPagedOptions, prependedOptions, appendedOptions, getOptionValue]);

  // Groups in arrival order. Appended options are grouped too and come last, so they end their group, like
  // discharge's "Other" at the end of "Additional providers".
  const groupedOptions = useMemo(() => {
    if (!groupBy) return allOptions;

    const groups: Record<string, T[]> = {};
    const groupOrder: string[] = [];

    for (const item of allOptions) {
      const key = groupBy(item);
      if (!groups[key]) {
        groups[key] = [];
        groupOrder.push(key);
      }
      groups[key].push(item);
    }

    return groupOrder.map((key) => ({
      label: getGroupOptionLabel?.(key) ?? key,
      options: groups[key],
    }));
  }, [allOptions, groupBy, getGroupOptionLabel]);

  useEffect(() => {
    if (debouncedSearch !== prevDebouncedSearch.current) {
      prevDebouncedSearch.current = debouncedSearch;
      onDebouncedSearch?.(debouncedSearch);
    }
  }, [debouncedSearch, onDebouncedSearch]);

  const selectProps: InfiniteScrollMenuListType = {
    hasMore: isPagedResource(options) ? options.hasNextPage : false,
    isLoading: isPagedResource(options) ? options.isLoading : false,
    onEndReached: function (): void {
      if (isPagedResource(options)) {
        options.fetchNextPage({ cancelRefetch: false });
      }
    },
    onMenuOpen: function (): void {
      // Waits for a first page already loading (a picker loaded before the menu opens) instead of restarting it.
      if (isPagedResource(options) && !options.isFetched) {
        options.fetchNextPage({ cancelRefetch: false });
      }
    },
    ...(isPagedResource(options) ? { filterOption: () => true } : {}), // whether or not to use client side filtering
    ...reactSelectProps,
  };
  const hasOptions = allOptions.length > 0;
  useEffect(() => {
    if (populateDefault && hasOptions && !value) {
      onChange(allOptions[0]);
    }
  }, [hasOptions, onChange, allOptions, value, populateDefault]);

  const select = (
    <ReactSelect
      isDisabled={disabled}
      placeholder={placeholder}
      options={groupedOptions}
      onChange={(x) => onChange(x as T)}
      getOptionValue={getOptionValue}
      getOptionLabel={getOptionLabel}
      formatOptionLabel={formatOptionLabel}
      value={value}
      onInputChange={setSearch}
      inputValue={search ?? ''}
      components={{
        LoadingIndicator: undefined,
        MenuList: InfiniteScrollMenuList as unknown as undefined, //react-select's typescript support is bad, and it brings out something ugly in me.
        Placeholder: ({ children: _, ...props }) => {
          return (
            <components.Placeholder {...props}>
              {props.isFocused ? (
                <StyledPlaceholder>{typeToSearchText}</StyledPlaceholder>
              ) : (
                <StyledPlaceholder>{placeholder}</StyledPlaceholder>
              )}
            </components.Placeholder>
          );
        },
      }}
      styles={getDefaultStyles()}
      theme={customTheme}
      loadingMessage={() => ''}
      {...(selectProps as unknown as Record<string, never>)}
    />
  );

  if (dataTestId) {
    return <div data-testid={dataTestId}>{select}</div>;
  }

  return select;
}

type MultiSelectChipsType<T> = Omit<SelectType<T>, 'value' | 'onChange' | 'populateDefault'> & {
  onChange: (change: T[]) => void;
  values?: T[] | null;
  title?: string;
  // A legend beside the title, e.g. one explaining an icon on the options.
  badge?: React.ReactNode;
};
export function MultiSelectChips<T>(props: MultiSelectChipsType<T>) {
  const {
    options,
    getOptionLabel,
    formatOptionLabel,
    getOptionValue,
    placeholder = 'Select...',
    onChange,
    values,
    disabled = false,
    prependedOptions = [],
    appendedOptions = [],
    onDebouncedSearch,
    title,
    badge,
    ...reactSelectProps
  } = props;

  const [search, setSearch] = useState<string>('');
  const debouncedSearch = useDebounce(search);
  const prevDebouncedSearch = useRef<string>(debouncedSearch);

  const pagedData = isPagedResource(options) ? options.data?.pages : undefined;
  const nonPagedOptions = isPagedResource(options) ? undefined : options;
  const allOptions = useMemo(() => {
    const base = pagedData ? pagedData.flatMap((p) => p.data) : (nonPagedOptions ?? []);
    return buildOptions(base, prependedOptions, appendedOptions, getOptionValue);
  }, [pagedData, nonPagedOptions, prependedOptions, appendedOptions, getOptionValue]);

  const selectProps: InfiniteScrollMenuListType = {
    hasMore: isPagedResource(options) ? options.hasNextPage : false,
    isLoading: isPagedResource(options) ? options.isLoading : false,
    onEndReached: function (): void {
      if (isPagedResource(options)) {
        options.fetchNextPage({ cancelRefetch: false });
      }
    },
    onMenuOpen: function (): void {
      // setTrackOpen(true);
      if (isPagedResource(options) && !options.isFetched) {
        options.fetchNextPage();
      }
    },
    ...(isPagedResource(options) ? { filterOption: () => true } : {}), // whether or not to use client side filtering
    ...reactSelectProps,
  };

  useEffect(() => {
    if (debouncedSearch !== prevDebouncedSearch.current) {
      prevDebouncedSearch.current = debouncedSearch;
      onDebouncedSearch?.(debouncedSearch);
    }
  }, [debouncedSearch, onDebouncedSearch]);

  const count = values?.length ?? 0;
  const titleWithCount = title ? (count > 0 ? `${title} - ${count} selected` : title) : undefined;

  return (
    <>
      {badge ? (
        <TitleRow>
          {titleWithCount && <FieldLabel>{titleWithCount}</FieldLabel>}
          {badge}
        </TitleRow>
      ) : (
        titleWithCount && <FieldLabel>{titleWithCount}</FieldLabel>
      )}
      <ReactSelect
        {...(selectProps as unknown as Record<string, never>)}
        isMulti
        isClearable
        isDisabled={disabled}
        placeholder={placeholder}
        options={allOptions}
        closeMenuOnSelect={false}
        blurInputOnSelect={false}
        onChange={(x) => onChange(x as T[])}
        getOptionValue={getOptionValue}
        getOptionLabel={getOptionLabel}
        formatOptionLabel={formatOptionLabel}
        onInputChange={setSearch}
        inputValue={search}
        value={values}
        components={{
          LoadingIndicator: undefined,
          MultiValue,
          ClearIndicator: (props: any) => (
            <components.ClearIndicator {...props}>
              <DropdownClearIcon width={10} height={10} />
            </components.ClearIndicator>
          ),
          MenuList: InfiniteScrollMenuList as unknown as undefined, //react-select's typescript support is bad, and it brings out something ugly in me.
          Placeholder: ({ children: _, ...props }) => {
            return (
              <components.Placeholder {...props}>
                {props.isFocused ? (
                  <StyledPlaceholder>Type to search...</StyledPlaceholder>
                ) : (
                  <StyledPlaceholder>{placeholder || 'Select...'}</StyledPlaceholder>
                )}
              </components.Placeholder>
            );
          },
        }}
        styles={{
          ...getDefaultStyles(),
          ...(reactSelectProps.menuPortalTarget ? { menuPortal: (base) => ({ ...base, zIndex: 9999 }) } : {}),
        }}
        theme={customTheme}
        loadingMessage={() => ''}
      />
    </>
  );
}

const StyledCheckbox = styled.div`
  align-items: flex-start;
  cursor: pointer;
  display: flex;
  padding: 8px 12px;
  & label {
    cursor: pointer;
  }
  &:hover {
    background-color: var(--primary-blue-25);
  }
`;
const CheckboxContainer = styled.div`
  align-items: center;
  display: flex;
  flex: 0;
  margin-top: -1px;
  min-width: 24px;
  min-height: 24px;
`;
const MultiSelectLeft = styled.span<{ $hasValue: boolean }>`
  color: ${({ $hasValue }) => ($hasValue ? colors.blue100 : colors.black50)};
`;
const MultiSelectRight = styled.span<{ $hasValue: boolean }>`
  color: ${({ $hasValue }) => ($hasValue ? colors.blue100 : colors.black100)};
`;
const StyledPlaceholder = styled.div<any>`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  width: 85%;
`;
export type MultiSelectType<T> = {
  options: T[] | UseInfiniteQueryResult<InfiniteData<Paginated<T>>>;
  getOptionValue: (obj: T) => string;
  getOptionLabel?: (obj: T) => string;
  formatOptionLabel?: (obj: T) => React.JSX.Element | null | string;
  placeholder?: string;
  onChange: (change: T[]) => void;
  value?: T[];
  prependedOptions?: T[];
  appendedOptions?: T[];
  onDebouncedSearch?: (search: string) => void;
};

// Defined once at module scope so they keep the same component identity across renders. Defined
// inline, every re-render (a parent's query resolving, a filter commit) swapped each option for a
// brand-new DOM node; a click whose mousedown landed on the old node then never fired, dropping the
// pick (OLIO-3576).
const multiSelectComponents = {
  LoadingIndicator: undefined,
  Option: ({ data, isSelected, children, selectOption }) => (
    <StyledCheckbox onClick={() => selectOption(data)}>
      <CheckboxContainer>
        {isSelected ? (
          <CheckboxOnIcon color={colors.blue100} width={14} height={14} />
        ) : (
          <CheckboxOffIcon color={colors.black100} width={14} height={14} />
        )}
      </CheckboxContainer>
      <BodySmall>{children}</BodySmall>
    </StyledCheckbox>
  ),
  MenuList: InfiniteScrollMenuList as unknown as undefined, //react-select's typescript support is bad, and it brings out something ugly in me.
  Placeholder: ({ children: _, ...props }: any) => {
    const selectedValues = props.getValue();
    return (
      <components.Placeholder {...props}>
        {props.isFocused && selectedValues.length == 0 ? (
          <StyledPlaceholder>
            <MultiSelectLeft $hasValue={false}>Type to search...</MultiSelectLeft>
          </StyledPlaceholder>
        ) : (
          <StyledPlaceholder>
            <MultiSelectLeft $hasValue={selectedValues.length > 0}>{props.selectProps.placeholder}: </MultiSelectLeft>
            <MultiSelectRight $hasValue={selectedValues.length > 0}>{selectedValues.length || 'All'}</MultiSelectRight>
          </StyledPlaceholder>
        )}
      </components.Placeholder>
    );
  },
  DropdownIndicator: (props: any) => (
    <components.DropdownIndicator {...props}>
      <DropdownCaretIcon />
    </components.DropdownIndicator>
  ),
  ClearIndicator: (props: any) => (
    <components.ClearIndicator {...props}>
      <DropdownClearIcon width={10} height={10} />
    </components.ClearIndicator>
  ),
};

export function MultiSelect<T>(props: MultiSelectType<T>) {
  const {
    options,
    getOptionLabel,
    formatOptionLabel,
    getOptionValue,
    placeholder = 'Select...',
    onChange,
    value,
    prependedOptions = [],
    appendedOptions = [],
    onDebouncedSearch,
    ...reactSelectProps
  } = props;

  const [search, setSearch] = useState<string>('');
  const debouncedSearch = useDebounce(search);
  const prevDebouncedSearch = useRef<string>(debouncedSearch);

  const pagedData = isPagedResource(options) ? options.data?.pages : undefined;
  const nonPagedOptions = isPagedResource(options) ? undefined : options;
  const allOptions = useMemo(() => {
    const base = pagedData ? pagedData.flatMap((p) => p.data) : (nonPagedOptions ?? []);
    return buildOptions(base, prependedOptions, appendedOptions, getOptionValue);
  }, [pagedData, nonPagedOptions, prependedOptions, appendedOptions, getOptionValue]);

  const selectProps = {
    hasMore: isPagedResource(options) ? options.hasNextPage : false,
    isLoading: isPagedResource(options) ? options.isLoading : false,
    onEndReached: function (): void {
      if (isPagedResource(options)) {
        options.fetchNextPage({ cancelRefetch: false });
      }
    },
    onMenuOpen: function (): void {
      if (isPagedResource(options) && !options.isFetched) {
        options.fetchNextPage();
      }
    },
    ...(isPagedResource(options) ? { filterOption: () => true } : {}), // whether or not to use client side filtering
    ...reactSelectProps,
  };

  useEffect(() => {
    if (debouncedSearch !== prevDebouncedSearch.current) {
      prevDebouncedSearch.current = debouncedSearch;
      onDebouncedSearch?.(debouncedSearch);
    }
  }, [debouncedSearch, onDebouncedSearch]);

  const handleInputChange = (val, { action }) => {
    if (action !== 'set-value') {
      setSearch(val);
    }
  };

  return (
    <ReactSelect
      isMulti
      isClearable
      {...(selectProps as unknown as Record<string, never>)}
      placeholder={placeholder}
      closeMenuOnSelect={false}
      blurInputOnSelect={false}
      hideSelectedOptions={false}
      controlShouldRenderValue={false}
      options={allOptions}
      onChange={(x) => onChange(x as T[])}
      onInputChange={handleInputChange}
      inputValue={search}
      getOptionValue={getOptionValue}
      getOptionLabel={getOptionLabel}
      formatOptionLabel={formatOptionLabel}
      value={value}
      loadingMessage={() => ''}
      components={multiSelectComponents}
      styles={getDefaultStyles(true)}
    />
  );
}

const TitleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 12px;
  line-height: 16px;
  color: var(--black-75);
`;
